import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { SwPartNumberState } from '../../../../../shared/enums/sw-part-number-state';
import { Dtc } from '../../../../../shared/models/dtc/dtc';
import { Policy } from '../../../../../shared/models/info/policy';
import { errorToString } from '../../../../common/common-utils';
import { AccessRightsService } from '../../../../services/access-rights.service';
import { DtcApiService } from '../../../../services/api/dtc-api.service';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { SwPartNumberService } from '../../../../services/sw-part-number.service';
import { EditTechnicalRedactionReviewableBase } from '../../../base/edit-technical-redaction-reviewable/edit-technical-redaction-reviewable-base';
import { ErrorMessageSelectionComponent } from '../../../common/error-message/error-message-selection/error-message-selection.component';
import { TextAreaComponent } from '../../../common/text-area/text-area.component';
import { FloppyIconComponent } from '../../../icons/floppy-icon/floppy-icon.component';
import { TrackUnsavedFieldDirective } from '../../../../directives/track-unsaved-field.directive';
import { LabelWithHelp } from '../../../common/label-with-help/label-with-help';
import { PropagateDtcChangesToEntitiesInOtherVersionsModal } from '../../../modals/propagate-changes-to-entities-in-other-versions-modal/propagate-dtc-changes-to-entities-in-other-versions-modal';
import { DtcUpdate } from '../../../../../shared/models/dtc/dtc-update';
import { EntityPropertyUpdatePropagationInfo } from '../../../../../shared/models/entity-property-info/internal/entity-property-update-propagation-info';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-dtc-edit',
  imports: [
    CommonModule,
    FormsModule,
    TextAreaComponent,
    ReactiveFormsModule,
    ErrorMessageSelectionComponent,
    FloppyIconComponent,
    TrackUnsavedFieldDirective,
    LabelWithHelp,
    PropagateDtcChangesToEntitiesInOtherVersionsModal,
    AgentActionDirective,
  ],
  templateUrl: './dtc-edit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dtc-edit.component.scss',
})
export class DtcEditComponent
  extends EditTechnicalRedactionReviewableBase<Dtc>
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Output()
  public dtcUpdated = new EventEmitter<Dtc>();

  @ViewChild('propagateChangesToEntitiesInOtherVersions')
  public propagateChangesToEntitiesInOtherVersionsModal?: PropagateDtcChangesToEntitiesInOtherVersionsModal;

  public elevatedEditDisabled = true;
  public basicEditDisabled = false;
  public updateTranslationFieldDisabled = true;
  public swPartNumberIsLocked = false;
  public errorMessageSelectionHasUnsavedChanges = false;

  protected override readonly formGroup: FormGroup<{
    priority: FormControl<number | null>;
    group: FormControl<number | null>;
    detailsOnTerminal: FormControl<string | null>;
    implementationHint: FormControl<string | null>;
    causeIntern: FormControl<string | null>;
    causeDealer: FormControl<string | null>;
    causeFarmer: FormControl<string | null>;
    remedyIntern: FormControl<string | null>;
    remedyDealer: FormControl<string | null>;
    remedyFarmer: FormControl<string | null>;
    errorMessageId: FormControl<string | null>;
    internalNotes: FormControl<string | null>;
  }>;

  private accessRightsService = inject(AccessRightsService);
  private dtcApi = inject(DtcApiService);
  private toast = inject(HotToastService);
  private swPartNumberService = inject(SwPartNumberService);
  private appInsightsService = inject(AppInsightsService);

  constructor() {
    super(STORAGE_KEYS.UNSAVED_DTC_CHANGES);
    this.formGroup = this.formBuilder.group({
      priority: new FormControl<number | null>(null, [Validators.required, Validators.min(0), Validators.max(255)]),
      group: new FormControl<number | null>(null, [Validators.required, Validators.min(0), Validators.max(255)]),
      detailsOnTerminal: new FormControl<string | null>(null, []),
      implementationHint: new FormControl<string | null>(null, []),
      causeIntern: new FormControl<string | null>(null, []),
      causeDealer: new FormControl<string | null>(null, []),
      causeFarmer: new FormControl<string | null>(null, []),
      remedyIntern: new FormControl<string | null>(null, []),
      remedyDealer: new FormControl<string | null>(null, []),
      remedyFarmer: new FormControl<string | null>(null, []),
      errorMessageId: new FormControl<string | null>(null, []),
      internalNotes: new FormControl<string | null>(null, []),
    });
  }

  public ngOnChanges(): void {
    this.appInsightsService.logSidebarUsage('DTC_edit');
    this.initializeDtcDetails();
    this.resetFieldTracking$.next(undefined);
    this.previousItemId = this.item?.id;
  }

  public requestPropagationOfChangesToEntitiesInOtherVersions(): void {
    this.item = this.getCurrentItemFromFormValues();
    this.propagateChangesToEntitiesInOtherVersionsModal?.openModal(this.item);
  }

  public get isNotDefaultLanguage(): boolean {
    return !this.translationService.defaultLanguageSelected();
  }

  protected confirmChanges(propagationInfos?: EntityPropertyUpdatePropagationInfo[]): void {
    if (this.item?.id) {
      const dtcUpdate: DtcUpdate = {
        dtc: this.getCurrentItemFromFormValues(),
        entityPropertyUpdateSynchronisationInfos: propagationInfos ?? [],
      };

      this.dtcApi.updateDtc(dtcUpdate).subscribe({
        next: (updatedDtc) => {
          this.toast.success('DTC updated successfully');
          this.dtcUpdated.emit(this.item);
          this.item!.rowVersion = updatedDtc.rowVersion;
          this.resetFieldTracking$.next(undefined);
        },
        error: (error) => {
          console.error(errorToString(error));
        },
      });
    }
  }

  protected override getCurrentItemFromFormValues(): Dtc {
    const formValues = this.formGroup.getRawValue();
    const currentDtc: Dtc = {
      ...this.item!,
      priority: formValues.priority ?? undefined,
      group: formValues.group ?? 255,
      detailsOnTerminal: formValues.detailsOnTerminal ?? undefined,
      implementationHint: formValues.implementationHint ?? undefined,
      causeIntern: formValues.causeIntern ?? undefined,
      causeDealer: formValues.causeDealer ?? undefined,
      causeFarmer: formValues.causeFarmer ?? undefined,
      remedyIntern: formValues.remedyIntern ?? undefined,
      remedyDealer: formValues.remedyDealer ?? undefined,
      remedyFarmer: formValues.remedyFarmer ?? undefined,
      errorMessageId: formValues.errorMessageId ?? undefined,
      internalNotes: formValues.internalNotes ?? undefined,
    };
    return currentDtc;
  }

  private initializeDtcDetails(): void {
    this.accessRightsService.getAccessRights$().subscribe((ar) => {
      this.elevatedEditDisabled = !ar.authorized(Policy.canUpdateElevated);
      this.basicEditDisabled = !ar.authorized(Policy.canUpdateBasic);
      this.updateTranslationFieldDisabled =
        !this.translationService.defaultLanguageSelected() && !ar.authorized(Policy.canUpdateTranslations);
    });
    this.configureDtcFormState();
  }

  private configureDtcFormState(): void {
    if (this.item) {
      this.formGroup.patchValue(this.item);
      const swPartNumberState = this.swPartNumberService.getSwPartNumber()?.state ?? SwPartNumberState.Snapshot;
      if (swPartNumberState == SwPartNumberState.Snapshot) {
        this.formGroup.disable();
        this.swPartNumberIsLocked = true;
      } else {
        this.formGroup.enable();
        this.swPartNumberIsLocked = false;
        this.formGroup.get('group')?.disable(); // always 255 and should not be editable

        if (swPartNumberState == SwPartNumberState.Released) {
          this.formGroup.get('detailsOnTerminal')?.disable();
          this.formGroup.get('implementationHint')?.disable();
          this.formGroup.get('priority')?.disable();
          this.formGroup.get('errorMessageId')?.disable();
        } else {
          this.formGroup.get('detailsOnTerminal')?.enable();
          this.formGroup.get('implementationHint')?.enable();
          this.formGroup.get('priority')?.enable();
          this.formGroup.get('errorMessageId')?.enable();
        }
      }
    }
  }
}
